import { ArrowLeft, Briefcase, Calendar, MapPin } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import Spinner from '../../components/Spinner';
import JobApplicationForm from '../../components/job-board/JobApplicationForm';
import { usePublicJob, usePublicJobBoard } from '../../hooks/useJobBoard';
import { JobDto } from '../../types/jobBoard';

const date = (value?: string) => value ? new Date(value).toLocaleDateString() : 'Not specified';
const value = (
  job: JobDto,
  field: 'industry' | 'country'
) =>
  field === 'industry'
    ? job.industryNames?.join(', ') ||
      'Not specified'
    : job.countryNames?.join(', ') ||
      'Not specified';

export default function PublicJobDetailsPage() {
  const { slug = '', jobId = '' } = useParams();
  const settings = usePublicJobBoard(slug);
  const job = usePublicJob(slug, jobId);
  const [applyOpen, setApplyOpen] = useState(false);
  if (settings.isLoading || job.isLoading) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  if (job.isError || !job.data) return <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center p-6 text-center"><h1 className="text-2xl font-bold text-slate-900">Job not found</h1><Link to={`/jobs/${slug}`} className="mt-4 text-strivre-burgundy">Back to job board</Link></div>;
  const item = job.data;
  return (
    <div
      className="w-full min-h-screen bg-slate-50 pb-20 md:pb-0"
      style={
        {
          "--job-primary": settings.data?.primaryColor || "#172554",
          "--job-secondary": settings.data?.secondaryColor || "#e2e8f0",
          "--job-button": settings.data?.buttonColor || "#7f1d1d",
          "--job-accent": settings.data?.accentColor || "#be123c",

          "--job-heading-text":
            settings.data?.headingTextColor || "#0f172a",

          "--job-body-text":
            settings.data?.bodyTextColor || "#475569",

          "--job-muted-text":
            settings.data?.mutedTextColor || "#94a3b8",

          backgroundColor:
            settings.data?.secondaryColor || "#e2e8f0",
        } as React.CSSProperties
      }
    >
      <header className="border-b border-slate-200 bg-white text-right">
        <div className="mx-auto max-w-5xl px-5 py-5 md:px-8">
          <Link
            to={`/jobs/${slug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
            style={{ color: "var(--job-accent)" }}
          >
            <ArrowLeft size={16} />
            All Positions
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 md:px-8">
        <div className="mb-8">
          <p
            className="text-sm font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--job-accent)" }}
          >
            Career Opportunity
          </p>

          <h1
            className="mt-2 text-3xl font-bold md:text-4xl"
            style={{ color: "var(--job-heading-text)" }}
          >
            {item.title}
          </h1>

          <div className="mt-4 flex flex-wrap gap-2">
            <span
              className="rounded-full px-3 py-1 text-xs font-semibold"
              style={{
                backgroundColor: `${settings.data?.accentColor || "#be123c"}20`,
                color: "var(--job-accent)"
              }}
            >
              {value(item, "industry")}
            </span>

            <span
              className="rounded-full px-3 py-1 text-xs font-semibold"
              style={{
                backgroundColor: `${settings.data?.accentColor || "#be123c"}20`,
                color: "var(--job-accent)"
              }}
            >
              {value(item, "country")}
            </span>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
          <article
            className="rounded-3xl border bg-white p-7 shadow-sm"
            style={{
              borderColor: "var(--job-secondary)",
            }}
          >
            <section>
              <h2
                className="text-xl font-bold"
                style={{ color: "var(--job-heading-text)" }}
              >
                Job Description
              </h2>

              <div
                className="mt-4 whitespace-pre-wrap text-left leading-7"
                style={{
                  color: "var(--job-body-text)",
                }}
              >
                {item.description}
              </div>
            </section>

            <section
              className="mt-8 border-t pt-8"
              style={{
                borderColor: "var(--job-secondary)",
              }}
            >
              <h2
                className="text-xl font-bold"
                style={{
                  color: "var(--job-heading-text)",
                }}
              >
                Requirements
              </h2>

              <div
                className="mt-4 whitespace-pre-wrap text-left leading-7"
                style={{
                  color: "var(--job-body-text)",
                }}
              >
                {item.requirements}
              </div>
            </section>

            {item.expirationDate && (
              <p
                className="mt-8 border-t pt-5 text-sm"
                style={{
                  borderColor: "var(--job-secondary)",
                  color: "var(--job-muted-text)",
                }}
              >
                Applications close {date(item.expirationDate)}.
              </p>
            )}
          </article>

          <aside>
            <div
              className="rounded-3xl border bg-white p-6 shadow-sm lg:sticky lg:top-6"
              style={{
                borderColor: "var(--job-secondary)",
              }}
            >
              <h2
                className="text-lg font-bold"
                style={{
                  color: "var(--job-heading-text)",
                }}
              >
                Interested in this role?
              </h2>

              <p
                className="mt-2 text-sm"
                style={{
                  color: "var(--job-body-text)",
                }}
              >
                Share your details and resume with the hiring team.
              </p>

              <div
                className="mt-5 space-y-3 text-sm"
                style={{
                  color: "var(--job-muted-text)",
                }}
              >
                <div className="flex items-center gap-2">
                  <Briefcase size={16} />
                  {value(item, "industry")}
                </div>

                <div className="flex items-center gap-2">
                  <MapPin size={16} />
                  {value(item, "country")}
                </div>

                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  Published {date(item.publishedDate)}
                </div>
              </div>

              <button onClick={() => setApplyOpen(true)} className="mt-6 w-full rounded-xl px-4 py-3 font-semibold text-white transition hover:opacity-90" style={{backgroundColor: "var(--job-button)" }}>
                Apply Now
              </button>
            </div>
          </aside>
        </div>
      </main>

      {applyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 pb-20 md:pb-0 my-auto">
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-white p-6 md:p-8"
            style={{
              borderColor: "var(--job-secondary)",
            }}
          >
            <h2
              className="mb-6 text-2xl font-bold"
              style={{
                color: "var(--job-heading-text)",
              }}
            >
              Apply for {item.title}
            </h2>

            <JobApplicationForm
              jobId={item.jobId}
              colors={settings.data}
              candidateDisclaimer={settings.data?.candidateDisclaimer}
              privacyPolicyUrl={settings.data?.privacyPolicyUrl}
              onClose={() => setApplyOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}